import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { differenceInSeconds } from 'date-fns';
import {
  isAConversationRoute,
  isAInboxViewRoute,
  isNotificationRoute,
} from 'dashboard/helper/routeHelpers';

const REFRESH_DEBOUNCE_MS = 250;
const MIN_REFRESH_INTERVAL_MS = 5000;
const MAX_RETRY_DELAY_MS = 30000;

// Support requests (message sync/cache revalidation) must not hold the
// single-flight lock forever after a laptop sleeps with a half-open request.
const withRecoveryTimeout = async (request, milliseconds) => {
  let timer;
  try {
    return await Promise.race([
      request,
      new Promise((_, reject) => {
        timer = setTimeout(
          () => reject(new Error('Recovery timed out')),
          milliseconds
        );
      }),
    ]);
  } finally {
    clearTimeout(timer);
  }
};

class ReconnectService {
  constructor(store, router) {
    this.store = store;
    this.router = router;
    this.disconnectTime = null;
    this.refreshTimer = null;
    this.refreshInFlight = null;
    this.refreshPending = false;
    this.lastRefreshAt = null;
    this.retryDelay = 1000;
    this.destroyed = false;
    this.notifyReconnect = false;

    this.setupEventListeners();
  }

  disconnect = () => {
    this.destroyed = true;
    this.refreshPending = false;
    this.clearRefreshTimer();
    this.removeEventListeners();
  };

  setupEventListeners = () => {
    window.addEventListener('online', this.handleOnlineEvent);
    window.addEventListener('focus', this.handleFocus);
    window.addEventListener('pageshow', this.handlePageShow);
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    emitter.on(BUS_EVENTS.WEBSOCKET_RECONNECT, this.onReconnect);
    emitter.on(BUS_EVENTS.WEBSOCKET_DISCONNECT, this.onDisconnect);
  };

  removeEventListeners = () => {
    window.removeEventListener('online', this.handleOnlineEvent);
    window.removeEventListener('focus', this.handleFocus);
    window.removeEventListener('pageshow', this.handlePageShow);
    document.removeEventListener(
      'visibilitychange',
      this.handleVisibilityChange
    );
    emitter.off(BUS_EVENTS.WEBSOCKET_RECONNECT, this.onReconnect);
    emitter.off(BUS_EVENTS.WEBSOCKET_DISCONNECT, this.onDisconnect);
  };

  getSecondsSinceDisconnect = () =>
    this.disconnectTime
      ? Math.max(differenceInSeconds(new Date(), this.disconnectTime), 0)
      : 0;

  // Browser push and Action Cable are independent. A tab may miss broadcasts
  // without ever observing a socket close. Reconcile on return, not just reopen.
  handleOnlineEvent = () => this.scheduleRefresh();

  handleFocus = () => this.scheduleRefresh();

  handlePageShow = event => {
    if (event.persisted) this.scheduleRefresh();
  };

  handleVisibilityChange = () => {
    if (document.hidden) {
      this.setConversationLastMessageId();
      this.clearRefreshTimer();
      return;
    }
    this.scheduleRefresh();
  };

  clearRefreshTimer = () => {
    clearTimeout(this.refreshTimer);
    this.refreshTimer = null;
  };

  scheduleRefresh = (delay = REFRESH_DEBOUNCE_MS) => {
    if (this.destroyed) return;
    this.refreshPending = true;
    if (
      document.hidden ||
      !navigator.onLine ||
      this.refreshInFlight ||
      this.refreshTimer
    )
      return;

    const cooldown =
      this.lastRefreshAt === null
        ? 0
        : MIN_REFRESH_INTERVAL_MS - (Date.now() - this.lastRefreshAt);
    this.refreshTimer = setTimeout(
      () => {
        this.refreshTimer = null;
        this.recover();
      },
      Math.max(delay, cooldown)
    );
  };

  // Read the latest page with the CURRENT filters. Never leave a global
  // updatedWithin filter behind, or change the user's infinite-scroll page.
  fetchConversations = () => this.store.dispatch('refreshConversationList');

  fetchFilteredOrSavedConversations = queryData =>
    this.store.dispatch('refreshConversationList', { queryData });

  fetchConversationsOnReconnect = async () => {
    const {
      getAppliedConversationFiltersQuery,
      'customViews/getActiveConversationFolder': activeFolder,
    } = this.store.getters;
    const query = getAppliedConversationFiltersQuery?.payload?.length
      ? getAppliedConversationFiltersQuery
      : activeFolder?.query;
    if (query) {
      return this.fetchFilteredOrSavedConversations(query);
    }
    return this.fetchConversations();
  };

  fetchConversationMessagesOnReconnect = async () => {
    const { conversation_id: conversationId } =
      this.router.currentRoute.value.params;
    if (conversationId) {
      await this.store.dispatch('syncActiveConversationMessages', {
        conversationId: Number(conversationId),
      });
    }
  };

  fetchNotificationsOnReconnect = async filter => {
    await this.store.dispatch('notifications/index', { ...filter, page: 1 });
  };

  revalidateCaches = async () => {
    const { label, inbox, team } = await this.store.dispatch(
      'accounts/getCacheKeys'
    );
    await Promise.all([
      this.store.dispatch('labels/revalidate', { newKey: label }),
      this.store.dispatch('inboxes/revalidate', { newKey: inbox }),
      this.store.dispatch('teams/revalidate', { newKey: team }),
    ]);
  };

  handleRouteSpecificFetch = async () => {
    const currentRoute = this.router.currentRoute.value.name;
    if (isAConversationRoute(currentRoute, true)) {
      const refreshed = await this.fetchConversationsOnReconnect();
      if (refreshed === false || this.destroyed) return false;
      if (!document.hidden) await this.fetchConversationMessagesOnReconnect();
    } else if (isAInboxViewRoute(currentRoute, true)) {
      await this.fetchNotificationsOnReconnect(
        this.store.getters['notifications/getNotificationFilters']
      );
    } else if (isNotificationRoute(currentRoute)) {
      await this.fetchNotificationsOnReconnect();
    }
    return true;
  };

  setConversationLastMessageId = async () => {
    const { conversation_id: conversationId } =
      this.router.currentRoute.value.params;
    if (conversationId) {
      await this.store.dispatch('setConversationLastMessageId', {
        conversationId: Number(conversationId),
      });
    }
  };

  onDisconnect = () => {
    if (!this.disconnectTime) {
      this.disconnectTime = new Date();
      this.setConversationLastMessageId();
    }
  };

  onReconnect = () => {
    this.notifyReconnect = true;
    return this.recover();
  };

  recover = () => {
    if (this.destroyed) return Promise.resolve(false);
    if (this.refreshInFlight) return this.refreshInFlight;
    if (
      document.hidden ||
      !navigator.onLine ||
      (this.lastRefreshAt !== null &&
        Date.now() - this.lastRefreshAt < MIN_REFRESH_INTERVAL_MS)
    ) {
      this.scheduleRefresh();
      return Promise.resolve(false);
    }
    this.clearRefreshTimer();
    this.refreshPending = false;
    this.refreshInFlight = this.performRefresh();
    return this.refreshInFlight;
  };

  performRefresh = async () => {
    const accountId = this.store.getters.getCurrentAccountId;
    let retryAfter = REFRESH_DEBOUNCE_MS;
    try {
      const refreshed = await withRecoveryTimeout(
        this.handleRouteSpecificFetch(),
        20000
      );
      if (this.destroyed) return false;
      if (
        refreshed === false ||
        accountId !== this.store.getters.getCurrentAccountId
      ) {
        this.refreshPending = true;
        return false;
      }
      await withRecoveryTimeout(this.revalidateCaches(), 10000);
      if (
        this.destroyed ||
        accountId !== this.store.getters.getCurrentAccountId
      )
        return false;
      this.lastRefreshAt = Date.now();
      this.retryDelay = 1000;
      this.disconnectTime = null;
      // A foreground HTTP refresh is not proof that the socket reconnected.
      // Only complete a notification that came from an actual reconnect event.
      if (this.notifyReconnect) {
        this.notifyReconnect = false;
        emitter.emit(BUS_EVENTS.WEBSOCKET_RECONNECT_COMPLETED);
      }
      return true;
    } catch (error) {
      // A transient HTTP failure must not leave the tab stale until F5.
      this.refreshPending = true;
      retryAfter = this.retryDelay;
      this.retryDelay = Math.min(this.retryDelay * 2, MAX_RETRY_DELAY_MS);
      return false;
    } finally {
      this.refreshInFlight = null;
      if (this.refreshPending) this.scheduleRefresh(retryAfter);
    }
  };
}

export default ReconnectService;
